import React from "react";
import { connect } from "react-redux";

import Button from "@material-ui/core/Button";
import MonacoEditor from "react-monaco-editor";
import { DebounceInput } from 'react-debounce-input';

import { openFile, setCurrentTab } from "./actions";
import { getStore } from "./store";
import { IFile, IState } from "./types";

interface IProps {
  code: string;
}

interface IEditorState {
  filter: string;
  timer: NodeJS.Timer | null;
  content: string;
  clicked: boolean;
  clickTime: number;
}

let editorSingleton: any = null;
let monacoSingleton: any = null;

export const openFileToLine = ({
  fileId,
  lineNumber
}: {
  fileId: number;
  lineNumber: number;
}): void => {
  const store = getStore();
  store.dispatch(setCurrentTab(0));
  store.dispatch(openFile(fileId));
  setTimeout(() => {
    editorSingleton.deltaDecorations(
      [],
      [
        {
          options: { inlineClassName: "lightBlueLine" }, // see index.css
          range: new monacoSingleton.Range(
            lineNumber + 1,
            1,
            lineNumber + 1,
            Infinity
          ) // array of lines is 0-indexed, editor line numbers are 1-indexed
        }
      ]
    );
    editorSingleton.revealLineInCenter(lineNumber);
  }, 50);
};

/**
 * @function filterByRegex
 * @param {string} lines a string that represents multiple lines of a text file delimited by the EOL character
 * @param {string | RegExp} regexExp a regular expression by which to compare each line
 * @description takes a single string containing multiple line-broken strings, filters each line by the regular expression,
 * and returns the filtered lines again delimited by the EOL character for continuity
 * @returns {string} string
 */
function filterByRegex(lines: string, regexExp: string | RegExp): string[] {
  const linesSplit = lines.split("\n");

  try {
    const regex: RegExp = new RegExp(regexExp);

    return linesSplit.filter(line => regex.test(line));
  } catch (error) {
    return linesSplit;
  }
}

class Editor extends React.Component<IProps, IEditorState> {
  protected editor: MonacoEditor | null;

  constructor(props: IProps) {
    super(props);
    this.editorDidMount = this.editorDidMount.bind(this);

    this.state = {
      filter: "",
      timer: null,
      content: this.props.code,
      clicked: false,
      clickTime: 0
    };
  }

  static getDerivedStateFromProps(props: IProps, state: IEditorState) {
    // TODO: optimize using memoization
    if (state.content !== props.code) {
      return {
        ...state,
        content: filterByRegex(props.code, state.filter).join("\n")
      };
    } else {
      return null;
    }
  }

  public editorDidMount(editor: any, monaco: any) {
    this.editor = editor;

    // Using onMouseDown because onDidChangeCursorPosition used previously also 
    // triggers when dragging while selecting text
    editor.onMouseDown((e: any) => {

      // Half second double click speed (4-5 hundred ms seems to be standard)
      const timeout = 500

      // update clicked to true and save click time
      if (this.state.clicked === false) {
        this.setState({
          clicked: true,
          clickTime: e.event.timestamp
        })
      }

      else if (this.state.clicked === true) {
        // if the diff of timestamps <= threshold treat as a double click
        if (e.event.timestamp - this.state.clickTime <= timeout) {

          // filter string reset and move editor to line number, 
          // also highlight the line that was double clicked
          const lineNumber = e.target.position.lineNumber;
          const { content } = this.state;
          
          // filter content to lineNumber
          const selectedLine = content
            .split("\n")
            .find((_, line) => line + 1 === lineNumber);
          
          // clear filter after selecting appropriate line
          if (selectedLine) {
            this.setState(
              {
                filter: ""
              },
              () => {
                this.filterContent();
  
                const unfilteredLineNumber: number = this.state.content
                  .split("\n")
                  .indexOf(selectedLine);
  
                if (unfilteredLineNumber >= 0) {
                  editor.setPosition({
                    column: 0,
                    lineNumber: unfilteredLineNumber + 1
                  });
  
                  editor.revealLineInCenter(unfilteredLineNumber + 1);
  
                  editor.setSelection({
                    startColumn: 0,
                    endColumn: selectedLine.length + 1,
                    startLineNumber: unfilteredLineNumber + 1,
                    endLineNumber: unfilteredLineNumber + 1
                  });
                }
              }
            );
          }
          this.setState({
            clicked: false,
            clickTime: 0
          })
        }
        // treat as a single click if diff over threshold
        else if (e.event.timestamp - this.state.clickTime > timeout) {
          this.setState({
            clicked: true,
            clickTime: e.event.timestamp
          })
        }
      }

    });
    editorSingleton = editor;
    monacoSingleton = monaco;
  }

  filterContent() {
    this.setState(() => ({
      content: filterByRegex(this.state.content, this.state.filter).join("\n")
    }));
  }

  /**
   * @function filterValueChanged
   * @description provides state management for the filter state property, debounced
   * @param event the onChange event fired by React when the text filter input changes
  //  */
  filterValueChanged = (event: any) => {
    const filterString = event.target.value;
    this.setState({
      filter: filterString
    });
    this.filterContent();
  };

  clearFilter = () => {
    const filterString = "";
    this.setState({
      filter: filterString
    });
    this.filterContent();
  };

  public render() {
    // const { code } = this.props;

    const options = {
      automaticLayout: true, // resize editor on window resize
      readOnly: true,
      scrollBeyondLastLine: false,
      selectOnLineNumbers: true
    };

    const { content, filter } = this.state;

    // To optimize this so that invalid regex expressions don't cause a re-render,
    // put this filtered value inside the component state instead of an inline computation here
    // const filteredCode = filter === '' ? content : filterByRegex(content, filter).join("\n");

    const filterWrapper = {
      display: "flex",
      margin: "0.5rem"
    } as React.CSSProperties;

    const inputStyle = {
      flex: 1,
      padding: "0.5rem 0.25rem",
      border: "1px solid #ccc",
      borderRadius: "4px",
      margin: "0.25rem",
      boxSizing: "border-box"
    } as React.CSSProperties;

    return (
      <React.Fragment>
        <div style={filterWrapper}>
          <DebounceInput
            style={inputStyle}
            id="filter-input"
            type="text"
            placeholder="Filter"
            value={filter}
            onChange={this.filterValueChanged}
            debounceTimeout={500}
          />
          <Button
            variant="contained"
            component="span"
            color={"primary"}
            onClick={this.clearFilter}
          >
            Clear
          </Button>
        </div>
        <MonacoEditor
          language={"ini"}
          theme={"vs-dark"}
          value={content}
          options={options}
          editorDidMount={this.editorDidMount}
        />
      </React.Fragment>
    );
  }
}

const mapStateToProps = (state: IState) => ({
  code: (state.files.find((file: IFile) => file.open) || { content: "" })
    .content
});

export default connect(mapStateToProps)(Editor);



// WEBPACK FOOTER //
// ./src/Editor.tsx